
import { forkJoin as observableForkJoin, Observable } from 'rxjs';
import {
  Component,
  Renderer2,
  OnInit
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Params,
  Router
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'ngx-webstorage';
import { AuthService } from './services/auth.service';
import { FeatureFlagService } from './services/feature-flag.service';
import { LanguageService } from './services/language.service';
import { MetaService } from './services/meta.service';
import { MessageService } from './services/message.service';
import { NotificationService } from './services/notification.service';
import { CookieService } from 'ngx-cookie-service';
import CONFIGURATION from '../configurations/configuration';
import { ENVIRONMENT } from '../environments/environment';
import { SwPush } from '@angular/service-worker';

import LogRocket from 'logrocket';

declare let CryptoJS, HmacSHA256: Function;

@Component({
  selector: 'modalku-app',
  templateUrl: './app.component.html'
})

export class AppComponent implements OnInit {
  showFooter: boolean;
  currentUrl: string;
  defaultMeta: any;
  isMaintenanceMode: boolean;
  isServiceDownMode: boolean;
  featureFlagObservable: Observable<any>;
  showIntercom: boolean;
  ENVIRONMENT: any;
  countryCode = CONFIGURATION.country_code;
  maintenanceMode: string;

  public constructor(
    private _activatedRoute: ActivatedRoute,
    private _authService: AuthService,
    private _languageService: LanguageService,
    private _localStorageService: LocalStorageService,
    private _metaService: MetaService,
    private messageService: MessageService,
    private _notificationService: NotificationService,
    private featureFlagService: FeatureFlagService,
    private _renderer: Renderer2,
    private _router: Router,
    private _translateService: TranslateService,
    private _cookieService: CookieService,
    private swPush: SwPush
  ) {
    // Maintenance Mode
    this.isMaintenanceMode = false;
    this.isServiceDownMode = false;
    this.showIntercom = true;
    this.ENVIRONMENT = ENVIRONMENT;
    // HOTFIX for no VN config, redirect to homepage(Contentful)
    if (window.location.hostname.indexOf('fundingsocieties.vn') > -1) {
      window.location.replace(`https://${window.location.hostname}`);
    }
  }

  ngOnInit() {
    const { intercomFlag, maintenanceMode } = this.featureFlagService.getFeatureFlagKeys();
    this.featureFlagObservable = this.featureFlagService.getFeatureFlagObservable();
    const intercomConfiguration = ENVIRONMENT.intercom[(this.countryCode).toLowerCase()];
    this.featureFlagObservable.subscribe((flags) => {
      this.showIntercom = flags[intercomFlag];
      if (this.showIntercom && CONFIGURATION.intercom.enable) {
        if (this._authService.getMemberId()) {
          const hash = CryptoJS.HmacSHA256(this._authService.getMemberId().toString(), intercomConfiguration.hashCode);
          const hashInHex = CryptoJS.enc.Hex.stringify(hash);
          window['Intercom']("boot", {
            app_id: intercomConfiguration.appId,
            email: this._authService.getUserName().toString(),
            user_id: this._authService.getMemberId().toString(),
            user_hash: hashInHex
          });
        } else {
          window['Intercom']("boot", { app_id: intercomConfiguration.appId });
          window['Intercom']("update");
        }
      }
      if (
        flags[maintenanceMode]?.mode &&
        flags[maintenanceMode]?.country?.[this.countryCode]
      ) {
        this.maintenanceMode = flags[maintenanceMode].mode;
      }
    });
    this.messageService.sendMessage('initializeLdClient');
    if (ENVIRONMENT.environment_name === 'production' && CONFIGURATION.country_code === 'SG') {
      LogRocket.init('e1vw0o/fs');
    }

    // Default Language
    this._activatedRoute
      .queryParams
      .subscribe((params: Params) => {
        const language = params['lang']
        if (language && language !== this._languageService.getDefaultLanguage()) {
          this._languageService.changeLanguage(language);
        }
        this._translateService.setDefaultLang(this._languageService.getDefaultLanguage());

        let utmParams = {
          utmSource: params['utm_source'],
          utmMedium: params['utm_medium'],
          utmCampaign: params['utm_campaign'],
          utmTerm: params['utm_term'],
          utmContent: params['utm_content'],
        }

        // https://stackoverflow.com/questions/281264/remove-empty-elements-from-an-array-in-javascript
        // Get the array of values, and filter out any falsy values, and see if the resultant array is empty.
        const hasNoUtmParams = Object.values(utmParams).filter(Boolean).length === 0;

        // If there are no UTM params, then we can set the utmParams to the localStorage value if there is one (otherwise an empty object).
        if (hasNoUtmParams) {
          utmParams = JSON.parse(localStorage.getItem('ngx-webstorage|utm')) || {};
        }

        this._localStorageService.store('utm', utmParams);
      });
    // Set the language according to the cookie locale if there is no query params
    // The locale cookie will set zh-CN, id-ID, or en-US
    // Will be mapping the locale cookie to zh, id, and en respectively to
    // support backward compatibility for v3
    let languageLocale = this._cookieService.get('locale');
    let localeMapping = this._languageService.getLocaleMapping();
    const compatibleLocale = languageLocale in localeMapping ? languageLocale : null;
    let searchParams = new URLSearchParams(location.search);
    if (!searchParams.has('lang') && compatibleLocale && compatibleLocale !== this._languageService.getDefaultLanguage()) {
      this._languageService.changeLanguage(compatibleLocale);
      this._translateService.setDefaultLang(this._languageService.getDefaultLanguage());
    }
    // Default Error Message
    this._translateService
      .get('form')
      .subscribe(response => {
        this._localStorageService.store('error-message', response['error']);
        this._localStorageService.store('unauthorized-message', response['unauthorized']);
        this._localStorageService.store('session-expired-reload', response['session-expired']['reload']);
        this._localStorageService.store('session-expired-relogin', response['session-expired']['relogin']);
        this._localStorageService.store('first-deposit-message', response['first-deposit']);
        this._localStorageService.store('activation-message', response['activation']);
      });

    // Router Event
    this._router
      .events
      .subscribe((e) => {
        if (e instanceof NavigationEnd) {
          window.scrollTo(0, 0);
          this.currentUrl = e.url;
          this.showFooter = this.currentUrl.indexOf('sign-up/forms') > -1
            || this.currentUrl.indexOf('admin-investor/subscription-agreement') > -1 ? true : false;
          this._injectMetaInformation();
          this._updateCanonical();
          this._authService
            .setDisplayPublicNavigationBar(this.currentUrl.indexOf('admin-') < 0);
        }
      });

    this._localStorageService
      .observe(this._authService.getExpiryTimeKey())
      .subscribe((data) => !data && this._authService.logOut());

    if (this._authService.getExpiryTime() !== '' && (new Date(this._authService.getExpiryTime()).getTime() <= Date.now()) && !this._authService.getRememberMe()) {
      this._notificationService.info('Your session has expired, you are auto logged out.');
      this._authService.logOut();
    }

    this.initializeGoogleTagManager();
    this.initializeCleverTap();
    this.initializeVwo();
    this.initializePushNotification();
    this.initializeCookieYes();
  }

  initializePushNotification() {
    const { webPushNotification } = this.featureFlagService.getFeatureFlagKeys();
    this.featureFlagObservable.subscribe((flags) => {
      const enableWebPushNotification = flags[webPushNotification];
      if (this.swPush.isEnabled && enableWebPushNotification) {
        this.swPush.requestSubscription({
          serverPublicKey: 'BPQcRu_DdErPjvEFu1Y5sJNzG-XbShf0VBiBw_c4yVoVJkSSWIIhbvoz9jnBaT4SVOThnHCXAqhvizwRTlDbBDE'
        })
          .then(subscription => {
            window['clevertap'].notifications.push({
              "titleText": 'Would you like to receive Push Notifications?',
              "bodyText": 'We promise to only send you relevant content and give you updates on your transactions',
              "okButtonText": 'Sign me up!',
              "rejectButtonText": 'No thanks',
              "okButtonColor": '#f28046',
              'serviceWorkerPath': 'ngsw-worker.js'
            });
          })
          .catch(console.error);
      }
    });
  }

  private _injectMetaInformation(): void {
    let baseUrl = 'meta';
    if (this.currentUrl !== '/') {
      baseUrl += this.currentUrl.replace(/\//g, '.');
    } else {
      baseUrl += '.default';
    }
    observableForkJoin(
      this._translateService.get('meta.default'),
      this._translateService.get(baseUrl)
    ).subscribe(response => {
      this.defaultMeta = response[0];
      let meta = response[1];
      this._metaService.setTitle(typeof meta.title !== 'undefined' ? meta.title : this.defaultMeta.title);
      this._metaService.setKeyword(this._renderer, typeof meta.keyword !== 'undefined'
        ? meta.keyword : this.defaultMeta.keyword);
      this._metaService.setDescription(this._renderer, typeof meta.description !== 'undefined'
        ? meta.description : this.defaultMeta.description);
      this._metaService.setOgTitle(this._renderer, typeof meta.title !== 'undefined'
        ? meta.title : this.defaultMeta.title);
      this._metaService.setOgUrl(this._renderer, typeof meta.title !== 'undefined'
        ? meta.url : this.defaultMeta.url);
      this._metaService.setOgDescription(this._renderer, typeof meta.description !== 'undefined'
        ? meta.description : this.defaultMeta.description);
      this._metaService.setTwitterDescription(this._renderer, typeof meta.description !== 'undefined'
        ? meta.description : this.defaultMeta.description);
    });
  }

  private _updateCanonical() {
    this._metaService.removeCanonical(this._renderer);

    if (CONFIGURATION.country_code === 'MY' && [
      '/sign-up-investor',
      '/progress',
      '/log-in/investor',
      '/log-in/borrower',
      '/log-in',
      '/admin-investor/referral'
    ].includes(this.currentUrl)) {
      this._metaService.addCanonical(this._renderer, this.currentUrl);
    }
  }

  private initializeCookieYes() {
    const src = ENVIRONMENT.cookieYesScript?.[this.countryCode.toLowerCase()];
    if (!src) return;

    const script = document.createElement('script');
    script.id = 'cookieyes';
    script.type = 'text/javascript';
    script.src = src;
    document.head.insertBefore(script, document.head.firstChild);
  }

  private initializeGoogleTagManager() {
    const dataLayerLabel = 'dataLayer';
    const gtmID = CONFIGURATION.google_tag_manager_id;
    const dataLayerParam = dataLayerLabel !== 'dataLayer' ? '&l=' + dataLayerLabel : '';

    window['dataLayer'] = window['dataLayer'] || [];
    window[dataLayerLabel].push({
      'gtm.start': new Date().getTime(),
      'event': 'gtm.js'
    });
    const firstScript = document.getElementsByTagName('script')[0];
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmID}${dataLayerParam}`;
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    firstScript.parentNode.insertBefore(script, firstScript);

    const searchGuruGtmId = CONFIGURATION.searchGuruGtmId;
    if (searchGuruGtmId) {
      const customScript = document.createElement('script');
      customScript.src = `https://www.googletagmanager.com/gtm.js?id=${searchGuruGtmId}`;
      customScript.type = 'text/javascript';
      customScript.async = true;
      customScript.defer = true;
      document.head.insertBefore(customScript, document.head.firstChild);

      const ifrm = document.createElement('iframe');
      ifrm.setAttribute(
        'src',
        `https://www.googletagmanager.com/gtm.js?id=${searchGuruGtmId}`
      );
      ifrm.style.width = '0';
      ifrm.style.height = '0';
      ifrm.style.display = 'none';
      ifrm.style.visibility = 'hidden';

      const noscript = document.createElement('noscript');
      noscript.id = 'gtmIframeNoScript';
      noscript.appendChild(ifrm);
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }

  private initializeCleverTap() {
    const cleverTapConfig = ENVIRONMENT.cleverTap[this.countryCode.toLowerCase()];
    if (cleverTapConfig && cleverTapConfig.projectId) {
      window['clevertap'] = { event: [], profile: [], region: 'sg1', account: [], onUserLogin: [], notifications: [], privacy: [] };
      window['clevertap'].account.push({ 'id': cleverTapConfig.projectId });
      const wzrk = document.createElement('script');
      wzrk.type = 'text/javascript';
      wzrk.async = true;
      wzrk.src = ('https:' == document.location.protocol ? 'https://d2r1yp2w7bby2u.cloudfront.net' : 'http://static.clevertap.com') + '/js/a.js?v=0';
      const s = document.getElementsByTagName('script')[0];
      s.parentNode.insertBefore(wzrk, s);
    }
  }

  private initializeVwo() {
    const accountId = ENVIRONMENT.vwo?.[this.countryCode.toLowerCase()]?.accountId;
    if (accountId) {
      const script = document.createElement('script');
      script.id = 'vwoCode';
      script.type = 'text/javascript';
      script.text = `
        window._vwo_code=window._vwo_code || (function() {
        var account_id=${accountId},
        version = 1.5,
        settings_tolerance=2000,
        library_tolerance=2500,
        use_existing_jquery=false,
        is_spa=1,
        hide_element='body',
        hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
        /* DO NOT EDIT BELOW THIS LINE */
        f=false,w=window,d=document,vwoCodeEl=d.querySelector('#vwoCode'),code={use_existing_jquery:function(){return use_existing_jquery},library_tolerance:function(){return library_tolerance},hide_element_style:function(){return'{'+hide_element_style+'}'},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=d.createElement('script');t.fetchPriority='high';t.src=e;t.type='text/javascript';t.onerror=function(){_vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(t)},getVersion:function(){return version},getMatchedCookies:function(e){var t=[];if(document.cookie){t=document.cookie.match(e)||[]}return t},getCombinationCookie:function(){var e=code.getMatchedCookies(/(?:^|;)\s?(_vis_opt_exp_\d+_combi=[^;$]*)/gi);e=e.map(function(e){try{var t=decodeURIComponent(e);if(!/_vis_opt_exp_\d+_combi=(?:\d+,?)+\s*$/.test(t)){return''}return t}catch(e){return''}});var i=[];e.forEach(function(e){var t=e.match(/([\d,]+)/g);t&&i.push(t.join('-'))});return i.join('|')},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;w.settings_timer=setTimeout(function(){_vwo_code.finish()},settings_tolerance);var e=d.currentScript,t=d.createElement('style'),i=e&&!e.async?hide_element?hide_element+'{'+hide_element_style+'}':'':code.lA=1,n=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');vwoCodeEl&&t.setAttribute('nonce',vwoCodeEl.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));n.appendChild(t);var o=this.getCombinationCookie();this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+ +is_spa+'&vn='+version+(o?'&c='+o:''));return settings_timer}};w._vwo_settings_timer = code.init();return code;}());
      `;
      document.head.appendChild(script);
    }
  }
}
